<template>
  <!-- This component is intended to run only on the client.
It has a .client suffix, which Nuxt uses to prevent the component from being loaded during server-side rendering — but this only applies when the component is auto-imported or imported from #components.
https://nuxt.com/docs/guide/directory-structure/components#client-components

Therefore, to ensure it's also not rendered on the server, it must be wrapped in a ClientOnly block in the template.  -->
  <ClientOnly>
    <Transition
      :enter-from-class="
        [slideTypes[slideType].enterClasses, 'backdrop:opacity-0'].join(' ')
      "
      :enter-to-class="
        [slideTypes[slideType].enterToClasses, 'backdrop:opacity-100'].join(' ')
      "
      enter-active-class="transform transition-all duration-200 backdrop:transition backdrop:ease-linear backdrop:duration-200"
      leave-active-class="transform transition-all duration-200 backdrop:transition backdrop:ease-linear backdrop:duration-200"
      :leave-from-class="
        [slideTypes[slideType].leaveClasses, 'backdrop:opacity-100'].join(' ')
      "
      :leave-to-class="
        [slideTypes[slideType].leaveToClasses, 'backdrop:opacity-0'].join(' ')
      "
    >
      <!-- eslint-disable-next-line vue/require-toggle-inside-transition  -->
      <dialog
        ref="slideIn"
        v-dialog.modal="isOpen"
        :name="name"
        class="h-full overflow-hidden transition-all backdrop:bg-black/50 max-md:bg-black/50 max-sm:m-0 max-sm:h-dvh max-sm:w-screen md:mr-0"
        :class="$attrs.class"
        @click="onClick"
        @cancel="onCancel"
      >
        <div
          class="w-full md:m-0 md:hidden"
          :class="{
            'max-md:h-2/3': mobileHeight === 'h-1/3',
            'max-md:h-1/2': mobileHeight === 'h-1/2',
            'max-md:h-1/3': mobileHeight === 'h-2/3',
            'max-md:h-1/4': mobileHeight === 'h-3/4',
            'max-md:h-1/5': mobileHeight === 'h-4/5',
            'max-md:h-1/6': mobileHeight === 'h-5/6',
            'max-md:h-0': mobileHeight === 'h-full',
          }"
          @click="toggle"
        />
        <div
          class="w-full overflow-y-auto rounded-xl bg-white scrollbar-hide md:inset-y-2 md:right-2 md:h-full md:min-w-[25rem] md:max-w-[25rem]"
          data-testid="slide-in-overflow"
          :class="[
            slideClass,
            {
              'md:rounded-xl': isRounded,
              'md:rounded-none': !isRounded,
              'max-md:h-1/3': mobileHeight === 'h-1/3',
              'max-md:h-1/2': mobileHeight === 'h-1/2',
              'max-md:h-2/3': mobileHeight === 'h-2/3',
              'max-md:h-3/4': mobileHeight === 'h-3/4',
              'max-md:h-4/5': mobileHeight === 'h-4/5',
              'max-md:h-5/6': mobileHeight === 'h-5/6',
              'max-md:h-full': mobileHeight === 'h-full',
            },
          ]"
        >
          <div class="relative flex max-h-full min-h-full flex-col">
            <slot v-bind="toggle" name="slide-in-content">
              <div
                class="sticky top-0 z-10 bg-white/90 px-6 py-4"
                :class="[
                  slideHeaderClass,
                  { 'border-b border-b-gray-200': !borderless },
                ]"
              >
                <slot name="slide-in-header" :toggle="toggle" />
              </div>
              <slot name="slide-in-body" />
              <div
                v-if="$slots['slide-in-actions']"
                class="sticky bottom-0 z-10 mt-auto bg-white p-6"
                :class="{ 'border-t border-t-gray-200': !borderless }"
              >
                <slot name="slide-in-actions" />
              </div>
            </slot>
          </div>
        </div>
      </dialog>
    </Transition>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { onKeyStroke } from '@vueuse/core'
import { tabbable } from 'tabbable'
import { vDialog } from '../../directives/dialog'
import { SlideInType, useSlideIn } from '#storefront-ui'
import { onBeforeRouteUpdate } from '#app/composables/router'
import { ClientOnly } from '#components'

const {
  slideClass = '',
  slideHeaderClass = '',
  slideType = SlideInType.DEFAULT,
  borderless = false,
  name,
  closeOnRouteChange = true,
  mobileHeight = 'h-2/3',
  isRounded = true,
} = defineProps<{
  name: string
  slideClass?: string
  slideHeaderClass?: string
  slideType?: SlideInType
  borderless?: boolean
  closeOnRouteChange?: boolean
  isRounded?: boolean
  mobileHeight?:
    | 'h-1/3'
    | 'h-1/2'
    | 'h-2/3'
    | 'h-3/4'
    | 'h-4/5'
    | 'h-5/6'
    | 'h-full'
}>()

const emit = defineEmits<{
  open: []
  close: []
}>()

const { isOpen, toggle, close } = useSlideIn(name)
const slideIn = useTemplateRef<HTMLDialogElement>('slideIn')

type SlideTypeClasses = Record<
  'enterClasses' | 'enterToClasses' | 'leaveClasses' | 'leaveToClasses',
  string
>

const slideTypes: Record<SlideInType, SlideTypeClasses> = {
  [SlideInType.DEFAULT]: {
    enterClasses: 'translate-y-full md:translate-y-0 md:translate-x-full',
    enterToClasses: 'translate-y-0 md:translate-x-0',
    leaveClasses: 'translate-y-0 md:translate-x-0',
    leaveToClasses: 'translate-y-full md:translate-y-0 md:translate-x-full',
  },
  [SlideInType.FROM_BOTTOM]: {
    enterClasses: 'translate-y-full',
    enterToClasses: 'translate-y-0',
    leaveClasses: 'translate-y-0',
    leaveToClasses: 'translate-y-full',
  },
}

const onClick = (e: MouseEvent) => {
  if (!isOpen.value) {
    return
  }

  // Close the dialog when the backdrop is clicked
  if (e.target instanceof Element && e.target?.nodeName === 'DIALOG') {
    close()
  }
}

// Intercept the cancel event that is triggered from other sources (e.g. escape keypress) then prevent
// the default behavior and use our own cancel behavior that uses an exit animation and keeps `isOpen` in sync
const onCancel = (e: Event) => {
  e.preventDefault()
  close()
}

const initialFocus = computed(() => {
  return slideIn.value && tabbable(slideIn.value).length ? undefined : false
})

const { activate: activateSlideInTrap, deactivate: deactivateSlideInTrap } =
  useFocusTrap(slideIn, {
    initialFocus: () => initialFocus.value,
    escapeDeactivates: false,
    immediate: isOpen.value,
  })

watch(isOpen, async (value) => {
  if (value) {
    await nextTick()
    activateSlideInTrap()
  } else {
    deactivateSlideInTrap()
  }
})

watch(isOpen, (newValue) => (newValue ? emit('open') : emit('close')))

onKeyStroke('Esc', () => close(), { target: slideIn })

// Whenever the route changes, we want to make sure that the slide-in is closed when `closeOnRouteChange` is `true`.
onBeforeRouteUpdate((to, from, next) => {
  if (closeOnRouteChange && isOpen.value) {
    close()
  }
  next()
})
</script>
